import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, AlertCircle, Loader2, Smartphone, QrCode, ShieldCheck, PencilLine, Copy, Check, Lightbulb } from "lucide-react";
import Logo from "@/components/shared/Logo";
import PhoneNumberField from "@/components/linking/PhoneNumberField";
import LinkingTrustCheckpoint from "@/components/linking/LinkingTrustCheckpoint";
import {
  createLinkingSession,
  getAttributionCode,
  getLinkingQr,
  recordLinkClientEvent,
  getRuntimeConfig,
  pollLinkStatus,
  requestPairingCode,
  startExistingWhatsAppOtp,
  trackEvent,
} from "@/lib/api";
import {
  clearLinkingPhonePrefill,
  deriveMaskedInternationalLinkingPhoneLabel,
  deriveMaskedLinkingPhoneLabel,
  getLinkingPhoneState,
  getRememberedLinkingPhonePrefill,
  inferDefaultPhoneCountry,
  normalizeLinkingPhonePrefill,
  rememberLinkingPhonePrefill,
} from "@/lib/linking-phone";
import {
  buildQrPreferredLinkUrl,
  clearShareLinkEntry,
  getShareLinkEntry,
  getShareLinkIdentityLabel,
  getShareLinkMaskedPhoneLabel,
  getShareLinkSocialProofLabel,
  getPreferredLinkingMethod,
  shouldOfferShareLinkConfirmation,
} from "@/lib/share-link-entry";
import { ONBOARDING_DESIGN_MODE } from "@/lib/onboarding-design-mode";
import { useI18n } from "@/lib/i18n";
import {
  resolveLinkingQrImageSrc,
  shouldBootstrapQrLinkSession,
  shouldFinalizeLinkedSuccess,
  shouldPromoteQrLinkToAwaitingLink,
} from "@/lib/account-linking";
import {
  TECH_LOCAL_URL,
  MIN_LINKING_NEXT_STEPS,
  MIN_LINKING_TRUST_CHECKPOINT_CONTENT,
  MIN_LINKING_WELCOME_CONTENT,
} from "@/lib/trust-content";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";

const STATES = {
  ready: "ready",
  awaiting_link: "awaiting_link",
  linked: "linked",
  expired: "expired",
  error: "error",
};

const OPEN_WHATSAPP_URL = "https://wa.me/";
const OPEN_WHATSAPP_FALLBACK_URL = "https://api.whatsapp.com/send";
const MOBILE_QR_MEDIA_QUERY = "(max-width: 767px)";

const LOCAL_HISTORY_VIEWS = {
  welcome: "welcome",
  intro: "intro",
  suggested: "suggested",
  otp: "otp",
  notify: "notify",
  manual: "manual",
  code: "code",
  waiting: "waiting",
  qr: "qr",
  ready: "ready",
};

function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function LinkingNextSteps() {
  const { tx } = useI18n();

  return (
    <div className="grid grid-cols-1 gap-2 text-left" data-testid="linking-next-steps">
      {MIN_LINKING_NEXT_STEPS.map((step, index) => (
        <div key={step.title} className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
          <div className="flex items-start gap-3">
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
              {index + 1}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{tx(step.title)}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{tx(step.detail)}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LinkingWelcomePanel({ disabled, onContinue }) {
  const { tx } = useI18n();

  return (
    <div className="flex w-full flex-col items-start gap-4 text-left" data-testid="linking-welcome-panel">
      <div className="w-full rounded-2xl border border-border/80 bg-muted/30 p-4" data-testid="linking-welcome-summary">
        <div className="space-y-3">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
              {tx(MIN_LINKING_WELCOME_CONTENT.receivedLabel)}
            </p>
            <p className="mt-1 text-base font-semibold leading-snug text-foreground">
              {tx(MIN_LINKING_WELCOME_CONTENT.receivedDetail)}
            </p>
          </div>
          <div className="h-px bg-border/70" />
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">
              {tx(MIN_LINKING_WELCOME_CONTENT.sentLabel)}
            </p>
            <p className="mt-1 text-base font-semibold leading-snug text-foreground">
              {tx(MIN_LINKING_WELCOME_CONTENT.sentDetail)}
            </p>
          </div>
        </div>
      </div>

      <div className="w-full rounded-2xl border border-border/80 bg-muted/20 p-4" data-testid="linking-welcome-security">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
            <ShieldCheck className="h-5 w-5" strokeWidth={2.1} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{tx(MIN_LINKING_WELCOME_CONTENT.securityTitle)}</p>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {tx(MIN_LINKING_WELCOME_CONTENT.securityDetail)}
            </p>
          </div>
        </div>
      </div>

      <Button
        className="h-12 w-full rounded-xl"
        onClick={onContinue}
        disabled={disabled}
        data-testid="linking-welcome-continue"
      >
        {tx("Continue")}
      </Button>

      <p className="w-full text-center text-xs leading-relaxed text-muted-foreground" data-testid="linking-welcome-creator">
        {tx("Created by")}{" "}
        <a
          href={TECH_LOCAL_URL}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-foreground/90 underline decoration-border underline-offset-4"
        >
          {tx(MIN_LINKING_WELCOME_CONTENT.creatorLabel)}
        </a>
        , {tx(MIN_LINKING_WELCOME_CONTENT.creatorDetail)}
      </p>
    </div>
  );
}

export default function QRLinking({ onSuccess, onBack, onSignIn, relinkAccountId = "", testSource = null, managementMode = false }) {
  const { tx } = useI18n();
  const [initialPhoneNumber] = useState(() => getRememberedLinkingPhonePrefill());
  const [shareLinkEntry] = useState(() => managementMode ? null : getShareLinkEntry());
  const [prefersQrEntry] = useState(() => (
    typeof window !== "undefined" && getPreferredLinkingMethod(window.location.search) === "qr"
  ));
  const shouldStartWithSuggestedAccount = !prefersQrEntry
    && !initialPhoneNumber
    && shouldOfferShareLinkConfirmation(shareLinkEntry);
  const rootLocalHistoryView = prefersQrEntry
    ? LOCAL_HISTORY_VIEWS.qr
    : shouldStartWithSuggestedAccount
      ? LOCAL_HISTORY_VIEWS.welcome
      : LOCAL_HISTORY_VIEWS.ready;
  const [defaultCountry, setDefaultCountry] = useState(() => inferDefaultPhoneCountry());
  const [linkState, setLinkState] = useState(STATES.ready);
  const [session, setSession] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [pairingCode, setPairingCode] = useState("");
  const [qrPayload, setQrPayload] = useState(null);
  const [showQrFallback, setShowQrFallback] = useState(prefersQrEntry);
  const [errorMessage, setErrorMessage] = useState("");
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isStartingExistingOtp, setIsStartingExistingOtp] = useState(false);
  const [isVerifyingExistingOtp, setIsVerifyingExistingOtp] = useState(false);
  const [isResettingSession, setIsResettingSession] = useState(false);
  const [pendingCodeRequestNumber, setPendingCodeRequestNumber] = useState("");
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("");
  const [showExistingOtpCard, setShowExistingOtpCard] = useState(false);
  const [existingOtpPhoneNumber, setExistingOtpPhoneNumber] = useState("");
  const [existingOtpPhoneNumberE164, setExistingOtpPhoneNumberE164] = useState("");
  const [existingOtpMaskedPhoneNumber, setExistingOtpMaskedPhoneNumber] = useState("");
  const [existingOtpChatLink, setExistingOtpChatLink] = useState("");
  const [existingOtpCode, setExistingOtpCode] = useState("");
  const [existingOtpError, setExistingOtpError] = useState("");
  const [isDesignLinkReady, setIsDesignLinkReady] = useState(false);
  const [showNotificationCheckpoint, setShowNotificationCheckpoint] = useState(false);
  const [showWaitingForConfirmation, setShowWaitingForConfirmation] = useState(false);
  const [showLinkWelcome, setShowLinkWelcome] = useState(() => shouldStartWithSuggestedAccount);
  const [showPairingCodeInstructions, setShowPairingCodeInstructions] = useState(true);
  const [showPairingCodeInstructionsToggle, setShowPairingCodeInstructionsToggle] = useState(false);
  const [showLinkIntro, setShowLinkIntro] = useState(false);
  const [isMobileQrLayout, setIsMobileQrLayout] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(MOBILE_QR_MEDIA_QUERY).matches;
  });
  const [copyLinkState, setCopyLinkState] = useState("idle");
  const [showSuggestedAccount, setShowSuggestedAccount] = useState(() => shouldStartWithSuggestedAccount);
  const sessionBootstrapStartedAtRef = useRef(0);
  const sessionBootstrapReadyTrackedRef = useRef(false);
  const sessionBootstrapFailureTrackedRef = useRef(false);
  const queuedCodeRequestStartedAtRef = useRef(0);
  const existingOtpAccountIdRef = useRef("");
  const existingOtpSetupModeRef = useRef("existing");
  const sessionClientTimingRef = useRef({ prefetched: false, prefetchLeadMs: null });
  const sessionCreationInFlightRef = useRef(null);
  const pairingCodeRequestedAtRef = useRef(0);
  const pairingCodeAttemptKeyRef = useRef("");
  const pairingCodeRenderedForAttemptRef = useRef("");
  const pairingCodeNextTrackedForAttemptRef = useRef("");
  // WhatsApp can react badly to repeated link/relink churn from the web app, so
  // both session bootstrap and QR loading must stay single-flight per live session.
  const qrRequestInFlightRef = useRef(false);

  const phoneState = getLinkingPhoneState(phoneNumber);
  const isSessionReadyForCode = session?.status === "awaiting_pairing_code" || session?.status === "awaiting_link";
  const isBootstrappingSession = !session || session.status === "starting";
  const canSubmitPhoneNumber = phoneState.canSubmit && linkState === STATES.ready;
  const isQueuedCodeRequest = Boolean(pendingCodeRequestNumber) && (!session?.linkSessionId || !isSessionReadyForCode);
  const isInteractionLocked = (
    isRequestingCode
    || isQueuedCodeRequest
    || isResettingSession
    || isStartingExistingOtp
    || isVerifyingExistingOtp
  );
  const isPendingCodeSubmission = isRequestingCode || isQueuedCodeRequest;
  const suggestedAccountLabel = getShareLinkIdentityLabel(shareLinkEntry);
  const suggestedMaskedPhoneLabel = getShareLinkMaskedPhoneLabel(shareLinkEntry);
  const suggestedSocialProofLabel = getShareLinkSocialProofLabel(shareLinkEntry);
  const normalizedEnteredPhone = normalizeLinkingPhonePrefill(phoneNumber);
  const isBlockedExistingMinAccount = shareLinkEntry?.prefillBlockedReason === "already_linked";
  const normalizedExistingOtpPhoneNumber = String(existingOtpPhoneNumber || "").replace(/\D+/g, "");
  const existingOtpMobileLink = normalizedExistingOtpPhoneNumber
    ? `whatsapp://send?phone=${normalizedExistingOtpPhoneNumber}`
    : "whatsapp://send";
  const existingOtpWebLink = existingOtpChatLink
    || (normalizedExistingOtpPhoneNumber ? `${OPEN_WHATSAPP_URL}${normalizedExistingOtpPhoneNumber}` : OPEN_WHATSAPP_URL);
  const selectedCodePhone = pendingCodeRequestNumber || selectedPhoneNumber || phoneState.normalizedPhoneNumber || phoneNumber;
  const maskedSelectedCodePhone = deriveMaskedLinkingPhoneLabel(selectedCodePhone, defaultCountry);
  const maskedSelectedInternationalPhone = deriveMaskedInternationalLinkingPhoneLabel(selectedCodePhone, defaultCountry);
  const helperText = isQueuedCodeRequest
    ? "Finishing your private WhatsApp connection. We’ll request the code as soon as it’s ready."
    : isBlockedExistingMinAccount && !normalizedEnteredPhone
    ? "That WhatsApp account already uses Min. Enter another number to continue."
    : "Pick the flag once, then type the rest. Pasting +countrycode switches it automatically.";
  const shouldShowQrMode = showQrFallback
    && !showLinkWelcome
    && !showLinkIntro
    && !showSuggestedAccount
    && !showExistingOtpCard
    && !showNotificationCheckpoint
    && !showWaitingForConfirmation;
  const activeCardView = linkState === STATES.error
    ? "error"
  : linkState === STATES.expired
      ? "expired"
      : showLinkWelcome
        ? "welcome"
      : showLinkIntro
        ? "intro"
      : showSuggestedAccount
        ? "suggested"
        : showExistingOtpCard
          ? "otp"
        : showNotificationCheckpoint
          ? "notify"
          : showWaitingForConfirmation
            ? "waiting"
          : shouldShowQrMode
            ? "qr"
            : linkState === STATES.awaiting_link
              ? "code"
              : "ready";
  const currentLocalHistoryView = typeof window !== "undefined"
    ? window.history.state?.mincoolLinkingView || ""
    : "";
  const canUseWhatsappScheme = typeof navigator !== "undefined"
    && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_QR_MEDIA_QUERY);
    const update = () => setIsMobileQrLayout(mediaQuery.matches);

    update();
    mediaQuery.addEventListener?.("change", update);
    return () => mediaQuery.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (copyLinkState !== "copied" || typeof window === "undefined") {
      return undefined;
    }
    const timer = window.setTimeout(() => setCopyLinkState("idle"), 2000);
    return () => window.clearTimeout(timer);
  }, [copyLinkState]);

  const pushLocalHistoryView = useCallback((view, extraState = {}) => {
    if (typeof window === "undefined") {
      return;
    }
    const currentState = window.history.state || {};
    window.history.pushState({ ...currentState, ...extraState, mincoolLinkingView: view }, "", window.location.href);
  }, []);

  const restoreWelcomeEntry = useCallback(() => {
    setShowLinkWelcome(true);
    setShowLinkIntro(false);
    setShowSuggestedAccount(true);
    setShowExistingOtpCard(false);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
  }, []);

  const restoreIntroEntry = useCallback(() => {
    setShowLinkWelcome(false);
    setShowLinkIntro(true);
    setShowSuggestedAccount(true);
    setShowExistingOtpCard(false);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
  }, []);

  const restoreSuggestedAccount = useCallback(() => {
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(true);
    setShowExistingOtpCard(false);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
    setExistingOtpCode("");
    setExistingOtpPhoneNumber("");
    setExistingOtpPhoneNumberE164("");
    setExistingOtpMaskedPhoneNumber("");
    setExistingOtpChatLink("");
    setPendingCodeRequestNumber("");
    setPairingCode("");
    setSelectedPhoneNumber("");
    setShowPairingCodeInstructions(true);
    setShowPairingCodeInstructionsToggle(false);
    setIsDesignLinkReady(false);
    setPhoneNumber("");
    queuedCodeRequestStartedAtRef.current = 0;
  }, []);

  const restoreManualEntry = useCallback(() => {
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowExistingOtpCard(false);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
    setExistingOtpCode("");
    setExistingOtpPhoneNumber("");
    setExistingOtpPhoneNumberE164("");
    setExistingOtpMaskedPhoneNumber("");
    setExistingOtpChatLink("");
    setPendingCodeRequestNumber("");
    setPairingCode("");
    setSelectedPhoneNumber("");
    setShowPairingCodeInstructions(true);
    setShowPairingCodeInstructionsToggle(false);
    setIsDesignLinkReady(false);
    setPhoneNumber("");
    queuedCodeRequestStartedAtRef.current = 0;
  }, []);

  const restoreNotificationCheckpoint = useCallback(() => {
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowExistingOtpCard(false);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(true);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
  }, []);

  const restoreExistingOtpEntry = useCallback(() => {
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowExistingOtpCard(true);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
  }, []);

  const restoreCodeEntry = useCallback(() => {
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowExistingOtpCard(false);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.awaiting_link);
    setErrorMessage("");
    setIsDesignLinkReady(Boolean(ONBOARDING_DESIGN_MODE && session?.status === "linked"));
  }, [session]);

  const restoreWaitingForConfirmation = useCallback(() => {
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(true);
    setLinkState(STATES.awaiting_link);
    setErrorMessage("");
    setExistingOtpError("");
    setIsDesignLinkReady(Boolean(ONBOARDING_DESIGN_MODE && session?.status === "linked"));
  }, [session]);

  const restoreQrEntry = useCallback(() => {
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowExistingOtpCard(false);
    setShowQrFallback(true);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
  }, []);

  const restoreReadyEntry = useCallback(() => {
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowExistingOtpCard(false);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
  }, []);

  const finalizeLinkedSuccess = useCallback(() => {
    const attributionCode = getAttributionCode();
    if (showQrFallback) {
      trackEvent("qr_scan_success", { code: attributionCode });
    }
    trackEvent("linked_device_success", {
      code: attributionCode,
      link_method: showQrFallback ? "qr" : "pairing_code",
    });
    clearLinkingPhonePrefill();
    clearShareLinkEntry({ preserveReferrerName: true });
    setLinkState(STATES.linked);
    onSuccess();
  }, [onSuccess, showQrFallback]);

  const trackSessionBootstrapReady = useCallback((status = "awaiting_pairing_code", timing = null) => {
    if (sessionBootstrapReadyTrackedRef.current || sessionBootstrapStartedAtRef.current === 0) {
      return;
    }
    sessionBootstrapReadyTrackedRef.current = true;
    trackEvent("link_session_ready", {
      code: getAttributionCode(),
      duration_ms: Math.round(nowMs() - sessionBootstrapStartedAtRef.current),
      status,
      prefetched: Boolean(sessionClientTimingRef.current.prefetched),
      prefetch_lead_ms: sessionClientTimingRef.current.prefetchLeadMs,
      server_poll_app_duration_ms: timing?.appDurationMs ?? null,
      server_poll_auth_duration_ms: timing?.authDurationMs ?? null,
      server_poll_waha_duration_ms: timing?.wahaDurationMs ?? null,
    });
  }, []);

  const trackSessionBootstrapFailure = useCallback((stage) => {
    if (sessionBootstrapFailureTrackedRef.current || sessionBootstrapStartedAtRef.current === 0) {
      return;
    }
    sessionBootstrapFailureTrackedRef.current = true;
    trackEvent("link_session_failed", {
      code: getAttributionCode(),
      duration_ms: Math.round(nowMs() - sessionBootstrapStartedAtRef.current),
      stage,
    });
  }, []);

  const resetLinkingState = useCallback(() => {
    sessionBootstrapStartedAtRef.current = nowMs();
    sessionBootstrapReadyTrackedRef.current = false;
    sessionBootstrapFailureTrackedRef.current = false;
    queuedCodeRequestStartedAtRef.current = 0;
    sessionClientTimingRef.current = { prefetched: false, prefetchLeadMs: null };
    sessionCreationInFlightRef.current = null;
    qrRequestInFlightRef.current = false;
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
    setExistingOtpCode("");
    setExistingOtpPhoneNumber("");
    setExistingOtpPhoneNumberE164("");
    setExistingOtpMaskedPhoneNumber("");
    setExistingOtpChatLink("");
    setPendingCodeRequestNumber("");
    setPairingCode("");
    setQrPayload(null);
    setSelectedPhoneNumber("");
    setIsDesignLinkReady(false);
    setShowLinkWelcome(shouldStartWithSuggestedAccount);
    setShowLinkIntro(false);
    setShowExistingOtpCard(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setSession(null);
    setShowQrFallback(prefersQrEntry);
    setShowSuggestedAccount(shouldStartWithSuggestedAccount);
    void getRuntimeConfig()
      .then((runtimeConfig) => {
        setDefaultCountry((currentCountry) => (
          inferDefaultPhoneCountry(undefined, runtimeConfig?.countryCode) || currentCountry
        ));
      })
      .catch(() => null);
  }, [prefersQrEntry, shouldStartWithSuggestedAccount]);

  const ensureLinkSession = useCallback(async () => {
    if (session?.linkSessionId) {
      return session;
    }
    if (sessionCreationInFlightRef.current) {
      return sessionCreationInFlightRef.current;
    }
    sessionCreationInFlightRef.current = (async () => {
      try {
        const created = await createLinkingSession({ relinkAccountId, testSource });
        sessionClientTimingRef.current = created.clientTiming || { prefetched: false, prefetchLeadMs: null };
        setSession(created);
        if (created.status !== "starting") {
          trackSessionBootstrapReady(created.status, created.timing);
        }
        return created;
      } catch (error) {
        trackSessionBootstrapFailure("create_link_session");
        setErrorMessage(error.message);
        setLinkState(STATES.error);
        return null;
      } finally {
        sessionCreationInFlightRef.current = null;
      }
    })();
    return sessionCreationInFlightRef.current;
  }, [relinkAccountId, session, testSource, trackSessionBootstrapFailure, trackSessionBootstrapReady]);

  const initSession = useCallback(async () => {
    if (isResettingSession) {
      return;
    }
    setIsResettingSession(true);
    resetLinkingState();
    try {
      const created = await createLinkingSession({ testSource });
      sessionClientTimingRef.current = created.clientTiming || { prefetched: false, prefetchLeadMs: null };
      setSession(created);
      setLinkState(STATES.ready);
      if (created.status !== "starting") {
        trackSessionBootstrapReady(created.status, created.timing);
      }
    } catch (error) {
      trackSessionBootstrapFailure("create_link_session");
      setErrorMessage(error.message);
      setLinkState(STATES.error);
    } finally {
      setIsResettingSession(false);
    }
  }, [isResettingSession, resetLinkingState, testSource, trackSessionBootstrapFailure, trackSessionBootstrapReady]);

  useEffect(() => {
    resetLinkingState();
  }, [resetLinkingState]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const currentState = window.history.state || {};
    if (currentState.mincoolLinkingView === rootLocalHistoryView) {
      return;
    }
    window.history.replaceState(
      { ...currentState, mincoolLinkingView: rootLocalHistoryView },
      "",
      window.location.href,
    );
  }, [rootLocalHistoryView]);

  useEffect(() => {
    trackEvent("phone_number_link_selected", {
      code: getAttributionCode(),
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handlePopState = (event) => {
      if (!shouldOfferShareLinkConfirmation(shareLinkEntry)) {
        return;
      }
      const view = event.state?.mincoolLinkingView;
      if (view === LOCAL_HISTORY_VIEWS.welcome) {
        restoreWelcomeEntry();
        return;
      }
      if (view === LOCAL_HISTORY_VIEWS.suggested) {
        restoreSuggestedAccount();
        return;
      }
      if (view === LOCAL_HISTORY_VIEWS.intro) {
        restoreIntroEntry();
        return;
      }
      if (view === LOCAL_HISTORY_VIEWS.otp) {
        restoreExistingOtpEntry();
        return;
      }
      if (view === LOCAL_HISTORY_VIEWS.manual) {
        restoreManualEntry();
        return;
      }
      if (view === LOCAL_HISTORY_VIEWS.notify) {
        restoreNotificationCheckpoint();
        return;
      }
      if (view === LOCAL_HISTORY_VIEWS.code) {
        restoreCodeEntry();
        return;
      }
      if (view === LOCAL_HISTORY_VIEWS.waiting) {
        restoreWaitingForConfirmation();
        return;
      }
      if (view === LOCAL_HISTORY_VIEWS.qr) {
        restoreQrEntry();
        return;
      }
      if (view === LOCAL_HISTORY_VIEWS.ready) {
        restoreReadyEntry();
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [
    restoreWelcomeEntry,
    restoreIntroEntry,
    restoreCodeEntry,
    restoreExistingOtpEntry,
    restoreManualEntry,
    restoreNotificationCheckpoint,
    restoreQrEntry,
    restoreReadyEntry,
    restoreSuggestedAccount,
    restoreWaitingForConfirmation,
    shareLinkEntry,
  ]);

  useEffect(() => {
    if (linkState !== STATES.ready || !session?.linkSessionId || session.status !== "starting") {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const result = await pollLinkStatus(session.linkSessionId);
        setSession((currentSession) => (
          currentSession
            ? {
              ...currentSession,
              status: result.status,
            }
            : currentSession
        ));
        if (result.status === "awaiting_pairing_code") {
          trackSessionBootstrapReady(result.status, result.timing);
          return;
        }
        if (result.status === "failed") {
          trackSessionBootstrapFailure("session_bootstrap");
          setErrorMessage("We couldn't prepare your WhatsApp session. Start a fresh link and try again.");
          setLinkState(STATES.error);
          return;
        }
        if (result.status === "expired") {
          setLinkState(STATES.expired);
        }
      } catch (error) {
        trackSessionBootstrapFailure("session_bootstrap_poll");
        setErrorMessage(error.message);
        setLinkState(STATES.error);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [linkState, session, trackSessionBootstrapFailure, trackSessionBootstrapReady]);

  useEffect(() => {
    if (!session) {
      return;
    }
    if (shouldPromoteQrLinkToAwaitingLink({
      showQrFallback,
      linkState,
      sessionStatus: session.status,
    })) {
      setLinkState(STATES.awaiting_link);
      return;
    }
    if (shouldFinalizeLinkedSuccess({
      showQrFallback,
      linkState,
      sessionStatus: session.status,
    })) {
      if (ONBOARDING_DESIGN_MODE) {
        setIsDesignLinkReady(true);
      } else {
        finalizeLinkedSuccess();
      }
    }
  }, [finalizeLinkedSuccess, linkState, session, showQrFallback]);

  useEffect(() => {
    const linkSessionId = String(session?.linkSessionId || "").trim();
    const attemptKey = String(pairingCodeAttemptKeyRef.current || "").trim();
    if (activeCardView !== "code" || !pairingCode || !linkSessionId || !attemptKey || pairingCodeRenderedForAttemptRef.current === attemptKey) {
      return;
    }
    pairingCodeRenderedForAttemptRef.current = attemptKey;
    const clientElapsedMs = pairingCodeRequestedAtRef.current
      ? Math.max(0, Math.round(nowMs() - pairingCodeRequestedAtRef.current))
      : null;
    void recordLinkClientEvent(linkSessionId, "pairing_code_rendered", {
      clientElapsedMs,
      metadata: {
        attemptKey,
        pairingCodePresent: true,
      },
    }).catch(() => {});
  }, [activeCardView, pairingCode, session]);

  useEffect(() => {
    if (linkState !== STATES.awaiting_link || !session) return;
    const interval = setInterval(async () => {
      try {
        const result = await pollLinkStatus(session.linkSessionId);
        setSession((currentSession) => (
          currentSession
            ? {
              ...currentSession,
              status: result.status,
            }
            : currentSession
        ));
        if (result.status === "linked") {
          if (ONBOARDING_DESIGN_MODE) {
            setIsDesignLinkReady(true);
          } else {
            finalizeLinkedSuccess();
          }
        } else if (result.status === "awaiting_pairing_code") {
          trackSessionBootstrapReady(result.status, result.timing);
        } else if (result.status === "expired") {
          setLinkState(STATES.expired);
        } else if (result.status === "failed") {
          setErrorMessage("WhatsApp rejected the link. Start a new code and try again.");
          setLinkState(STATES.error);
        } else if (result.status === "awaiting_link") {
          setLinkState(STATES.awaiting_link);
        } else if (result.status === "starting") {
          setLinkState(STATES.awaiting_link);
        }
      } catch (error) {
        setErrorMessage(error.message);
        setLinkState(STATES.error);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [finalizeLinkedSuccess, linkState, session, trackSessionBootstrapReady]);

  const loadQrPayload = useCallback(async (linkSessionId) => {
    if (!linkSessionId || qrRequestInFlightRef.current) {
      return;
    }

    qrRequestInFlightRef.current = true;
    try {
      const payload = await getLinkingQr(linkSessionId);
      if (!resolveLinkingQrImageSrc(payload)) {
        setErrorMessage("We couldn't render the WhatsApp QR code. Try again.");
        setLinkState(STATES.error);
        return;
      }
      setQrPayload(payload);
    } catch (error) {
      setErrorMessage(error.message);
      setLinkState(STATES.error);
    } finally {
      qrRequestInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!shouldBootstrapQrLinkSession({
      showQrFallback,
      linkSessionId: session?.linkSessionId,
      qrPayload,
    })) {
      return;
    }

    void ensureLinkSession();
  }, [ensureLinkSession, qrPayload, session?.linkSessionId, showQrFallback]);

  useEffect(() => {
    if (!showQrFallback || !session?.linkSessionId || qrPayload) {
      return;
    }

    void loadQrPayload(session.linkSessionId);
  }, [loadQrPayload, qrPayload, session, showQrFallback]);

  const handleRequestCode = useCallback(async (targetPhoneNumber = phoneNumber, options = {}) => {
    const {
      checkpointAfterRequest = true,
      pushHistoryView = true,
    } = options;
    const targetPhoneState = getLinkingPhoneState(targetPhoneNumber);
    if (!targetPhoneState.canSubmit) return;
    const normalizedPhoneNumber = targetPhoneState.normalizedPhoneNumber;
    const nextHistoryView = checkpointAfterRequest ? LOCAL_HISTORY_VIEWS.notify : LOCAL_HISTORY_VIEWS.code;
    setPendingCodeRequestNumber(normalizedPhoneNumber);
    queuedCodeRequestStartedAtRef.current = nowMs();
    setErrorMessage("");
    setSelectedPhoneNumber(normalizedPhoneNumber);
    const preparedSession = session?.linkSessionId ? session : await ensureLinkSession();
    if (!preparedSession) {
      setPendingCodeRequestNumber("");
      queuedCodeRequestStartedAtRef.current = 0;
      return;
    }
    if (!isSessionReadyForCode && preparedSession.status !== "awaiting_pairing_code" && preparedSession.status !== "awaiting_link") {
      return;
    }
    const waitedForSessionMs = queuedCodeRequestStartedAtRef.current
      ? Math.max(0, Math.round(nowMs() - queuedCodeRequestStartedAtRef.current))
      : null;
    try {
      setIsRequestingCode(true);
      const requestStartedAtMs = nowMs();
      pairingCodeRequestedAtRef.current = requestStartedAtMs;
      pairingCodeAttemptKeyRef.current = `${preparedSession.linkSessionId}:${requestStartedAtMs}`;
      setIsDesignLinkReady(false);
      setShowExistingOtpCard(false);
      setExistingOtpError("");
      setShowWaitingForConfirmation(false);
      setLinkState(checkpointAfterRequest ? STATES.ready : STATES.awaiting_link);
      setPendingCodeRequestNumber("");
      queuedCodeRequestStartedAtRef.current = 0;
      setErrorMessage("");
      const result = await requestPairingCode(
        preparedSession.linkSessionId,
        normalizedPhoneNumber,
        {
          queuedDuringBootstrap: waitedForSessionMs !== null,
          waitedForSessionMs,
        },
      );
      setSession((currentSession) => (
        currentSession
          ? {
            ...currentSession,
            status: "awaiting_link",
          }
          : currentSession
      ));
      setPairingCode(result.code || result.pairingCode || "");
      if (
        pushHistoryView
        && typeof window !== "undefined"
        && window.history.state?.mincoolLinkingView !== nextHistoryView
      ) {
        pushLocalHistoryView(nextHistoryView);
      }
      setShowSuggestedAccount(false);
      setShowQrFallback(false);
      setShowNotificationCheckpoint(checkpointAfterRequest);
      setShowWaitingForConfirmation(false);
      setLinkState(checkpointAfterRequest ? STATES.ready : STATES.awaiting_link);
    } catch (error) {
      setErrorMessage(error.message);
      setPendingCodeRequestNumber("");
      queuedCodeRequestStartedAtRef.current = 0;
      setLinkState(STATES.error);
    } finally {
      setIsRequestingCode(false);
    }
  }, [ensureLinkSession, isSessionReadyForCode, phoneNumber, pushLocalHistoryView, session]);

  useEffect(() => {
    if (
      !pendingCodeRequestNumber
      || (linkState !== STATES.ready && linkState !== STATES.awaiting_link)
      || !session?.linkSessionId
      || !isSessionReadyForCode
      || isRequestingCode
    ) {
      return;
    }

    void handleRequestCode(pendingCodeRequestNumber);
  }, [handleRequestCode, isRequestingCode, isSessionReadyForCode, linkState, pendingCodeRequestNumber, session]);

  const handleShowQr = async () => {
    const preparedSession = session?.linkSessionId ? session : await ensureLinkSession();
    if (!preparedSession?.linkSessionId) {
      return;
    }
    if (isInteractionLocked) return;
    const currentView = currentLocalHistoryView && currentLocalHistoryView !== LOCAL_HISTORY_VIEWS.qr
      ? currentLocalHistoryView
      : activeCardView === "waiting"
        ? LOCAL_HISTORY_VIEWS.waiting
      : activeCardView === "code"
        ? LOCAL_HISTORY_VIEWS.code
        : activeCardView === "otp"
          ? LOCAL_HISTORY_VIEWS.otp
        : activeCardView === "welcome"
          ? LOCAL_HISTORY_VIEWS.welcome
        : activeCardView === "intro"
          ? LOCAL_HISTORY_VIEWS.intro
        : activeCardView === "notify"
          ? LOCAL_HISTORY_VIEWS.notify
          : activeCardView === "suggested"
            ? LOCAL_HISTORY_VIEWS.suggested
            : LOCAL_HISTORY_VIEWS.ready;
    pushLocalHistoryView(LOCAL_HISTORY_VIEWS.qr, { mincoolQrReturnView: currentView });
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowExistingOtpCard(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setShowQrFallback(true);
    if (qrPayload) return;
    void loadQrPayload(preparedSession.linkSessionId);
  };

  const qrImage = resolveLinkingQrImageSrc(qrPayload);

  const handlePhoneNumberChange = (nextPhoneNumber) => {
    if (isInteractionLocked) {
      return;
    }
    setPhoneNumber(nextPhoneNumber);
    rememberLinkingPhonePrefill(nextPhoneNumber);
    queuedCodeRequestStartedAtRef.current = 0;
    setPendingCodeRequestNumber("");
    setExistingOtpError("");
  };

  const handleShowCodeEntry = () => {
    if (isInteractionLocked) {
      return;
    }
    if (typeof window !== "undefined" && currentLocalHistoryView === LOCAL_HISTORY_VIEWS.qr) {
      const currentState = window.history.state || {};
      const qrReturnView = currentState.mincoolQrReturnView || LOCAL_HISTORY_VIEWS.ready;

      if (qrReturnView === LOCAL_HISTORY_VIEWS.welcome) {
        restoreWelcomeEntry();
      } else if (qrReturnView === LOCAL_HISTORY_VIEWS.suggested) {
        restoreSuggestedAccount();
      } else if (qrReturnView === LOCAL_HISTORY_VIEWS.intro) {
        restoreIntroEntry();
      } else if (qrReturnView === LOCAL_HISTORY_VIEWS.otp) {
        restoreExistingOtpEntry();
      } else if (qrReturnView === LOCAL_HISTORY_VIEWS.notify) {
        restoreNotificationCheckpoint();
      } else if (qrReturnView === LOCAL_HISTORY_VIEWS.manual) {
        restoreManualEntry();
      } else if (qrReturnView === LOCAL_HISTORY_VIEWS.code) {
        restoreCodeEntry();
      } else if (qrReturnView === LOCAL_HISTORY_VIEWS.waiting) {
        restoreWaitingForConfirmation();
      } else {
        restoreReadyEntry();
      }

      window.history.replaceState(
        { ...currentState, mincoolLinkingView: qrReturnView },
        "",
        window.location.href,
      );
      return;
    }
    restoreReadyEntry();
  };

  const handleCopyQrLink = async () => {
    if (typeof window === "undefined") {
      return;
    }

    const href = buildQrPreferredLinkUrl(window.location.href);
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(href);
      } else {
        const temporaryInput = document.createElement("textarea");
        temporaryInput.value = href;
        temporaryInput.setAttribute("readonly", "");
        temporaryInput.style.position = "absolute";
        temporaryInput.style.left = "-9999px";
        document.body.appendChild(temporaryInput);
        temporaryInput.select();
        document.execCommand("copy");
        document.body.removeChild(temporaryInput);
      }
      setCopyLinkState("copied");
    } catch {
      setCopyLinkState("idle");
    }
  };

  const handleOpenWhatsApp = useCallback((event) => {
    if (!canUseWhatsappScheme || typeof window === "undefined") {
      return;
    }

    event.preventDefault();

    let cancelled = false;
    const fallbackTimer = window.setTimeout(() => {
      if (!cancelled) {
        window.location.assign(OPEN_WHATSAPP_FALLBACK_URL);
      }
    }, 900);

    const cancelFallback = () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", cancelFallback);
      window.removeEventListener("blur", cancelFallback);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        cancelFallback();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", cancelFallback, { once: true });
    window.addEventListener("blur", cancelFallback, { once: true });
    window.location.assign("whatsapp://send");
  }, [canUseWhatsappScheme]);

  const handleOpenExistingOtpWhatsApp = useCallback((event) => {
    if (!canUseWhatsappScheme || typeof window === "undefined") {
      return;
    }

    event.preventDefault();

    let cancelled = false;
    const fallbackTimer = window.setTimeout(() => {
      if (!cancelled) {
        window.location.assign(existingOtpWebLink);
      }
    }, 900);

    const cancelFallback = () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", cancelFallback);
      window.removeEventListener("blur", cancelFallback);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        cancelFallback();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", cancelFallback, { once: true });
    window.addEventListener("blur", cancelFallback, { once: true });
    window.location.assign(existingOtpMobileLink);
  }, [canUseWhatsappScheme, existingOtpMobileLink, existingOtpWebLink]);

  const handleChangeNumber = () => {
    if (isInteractionLocked) {
      return;
    }
    pushLocalHistoryView(LOCAL_HISTORY_VIEWS.manual);
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
    setShowQrFallback(false);
    setShowExistingOtpCard(false);
    setIsDesignLinkReady(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    queuedCodeRequestStartedAtRef.current = 0;
    setPendingCodeRequestNumber("");
    setPhoneNumber("");
    clearLinkingPhonePrefill();
  };

  const handleUseSuggestedAccount = () => {
    if (isInteractionLocked) {
      return;
    }
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    if (isBlockedExistingMinAccount) {
      const shareLinkCode = String(shareLinkEntry?.code || "").trim();
      if (!shareLinkCode) {
        setExistingOtpError("This share link is missing the recovery code. Use email instead.");
        return;
      }
      setIsStartingExistingOtp(true);
      setExistingOtpError("");
      existingOtpAccountIdRef.current = "";
      existingOtpSetupModeRef.current = "existing";
      void startExistingWhatsAppOtp({ code: shareLinkCode })
        .then((payload) => {
          setShowSuggestedAccount(false);
          setShowExistingOtpCard(true);
          setShowNotificationCheckpoint(false);
          setShowQrFallback(false);
          setLinkState(STATES.ready);
          setExistingOtpCode("");
          setExistingOtpError("");
          setExistingOtpPhoneNumber(String(payload?.phoneNumber || "").trim());
          setExistingOtpPhoneNumberE164(String(payload?.phoneNumberE164 || "").trim());
          setExistingOtpMaskedPhoneNumber(String(payload?.maskedPhoneNumber || "").trim());
          setExistingOtpChatLink(String(payload?.openWhatsAppUrl || "").trim());
          existingOtpAccountIdRef.current = String(payload?.accountId || "").trim();
          existingOtpSetupModeRef.current = String(payload?.setupMode || "").trim().toLowerCase() === "new" ? "new" : "existing";
          trackEvent("existing_whatsapp_otp_started", {
            code: getAttributionCode(),
            source: "share_link",
          });
          if (
            typeof window !== "undefined"
            && window.history.state?.mincoolLinkingView !== LOCAL_HISTORY_VIEWS.otp
          ) {
            pushLocalHistoryView(LOCAL_HISTORY_VIEWS.otp);
          }
        })
        .catch((error) => {
          setExistingOtpError(tx(error.message || "Could not send a code to WhatsApp."));
        })
        .finally(() => {
          setIsStartingExistingOtp(false);
        });
      return;
    }
    const nextPhoneNumber = shareLinkEntry?.prefilledPhoneNumber || "";
    if (!nextPhoneNumber) {
      return;
    }
    setPhoneNumber("");
    clearLinkingPhonePrefill();
    void handleRequestCode(nextPhoneNumber, { checkpointAfterRequest: true });
  };

  const handleContinueToCodeAfterNotification = ({
    showFullInstructions = true,
    showInstructionsToggle = false,
  } = {}) => {
    if (isInteractionLocked) {
      return;
    }
    if (typeof window !== "undefined" && window.history.state?.mincoolLinkingView !== LOCAL_HISTORY_VIEWS.code) {
      pushLocalHistoryView(LOCAL_HISTORY_VIEWS.code);
    }
    setShowPairingCodeInstructions(showFullInstructions);
    setShowPairingCodeInstructionsToggle(showInstructionsToggle);
    setShowNotificationCheckpoint(false);
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.awaiting_link);
  };

  const handleContinueToWaitingForConfirmation = () => {
    if (isInteractionLocked || !pairingCode) {
      return;
    }
    const linkSessionId = String(session?.linkSessionId || "").trim();
    const attemptKey = String(pairingCodeAttemptKeyRef.current || "").trim();
    if (linkSessionId && attemptKey && pairingCodeNextTrackedForAttemptRef.current !== attemptKey) {
      pairingCodeNextTrackedForAttemptRef.current = attemptKey;
      const clientElapsedMs = pairingCodeRequestedAtRef.current
        ? Math.max(0, Math.round(nowMs() - pairingCodeRequestedAtRef.current))
        : null;
      void recordLinkClientEvent(linkSessionId, "pairing_code_next_clicked", {
        clientElapsedMs,
        metadata: {
          attemptKey,
        },
      }).catch(() => {});
    }
    if (typeof window !== "undefined" && window.history.state?.mincoolLinkingView !== LOCAL_HISTORY_VIEWS.waiting) {
      pushLocalHistoryView(LOCAL_HISTORY_VIEWS.waiting);
    }
    setShowNotificationCheckpoint(false);
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowWaitingForConfirmation(true);
    setLinkState(STATES.awaiting_link);
  };

  const handleUseAnotherAccount = () => {
    if (isInteractionLocked) {
      return;
    }
    pushLocalHistoryView(LOCAL_HISTORY_VIEWS.manual);
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    clearLinkingPhonePrefill();
    setPhoneNumber("");
    setErrorMessage("");
    setExistingOtpError("");
    setShowExistingOtpCard(false);
    setPendingCodeRequestNumber("");
    setSelectedPhoneNumber("");
    queuedCodeRequestStartedAtRef.current = 0;
  };

  const handleExistingOtpCodeChange = (event) => {
    const normalized = String(event.target.value || "").replace(/\D+/g, "").slice(0, 6);
    setExistingOtpCode(normalized);
    setExistingOtpError("");
  };

  const handleUseEmailInstead = () => {
    if (isInteractionLocked) {
      return;
    }
    trackEvent("existing_whatsapp_otp_email_fallback", {
      code: getAttributionCode(),
    });
    onSignIn();
  };

  const handleVerifyExistingOtp = async () => {
    if (isInteractionLocked) {
      return;
    }
    const normalizedCode = String(existingOtpCode || "").replace(/\D+/g, "").slice(0, 6);
    if (normalizedCode.length !== 6) {
      setExistingOtpError("Enter the 6-digit code from WhatsApp.");
      return;
    }
    const client = getSupabaseBrowserClient();
    if (!client) {
      setExistingOtpError("Supabase is not configured.");
      return;
    }
    const verifyPhoneNumber = String(existingOtpPhoneNumberE164 || existingOtpPhoneNumber || "").trim();
    if (!verifyPhoneNumber) {
      setExistingOtpError("The linked WhatsApp number could not be resolved.");
      return;
    }

    setIsVerifyingExistingOtp(true);
    setExistingOtpError("");
    try {
      const { error } = await client.auth.verifyOtp({
        phone: verifyPhoneNumber,
        token: normalizedCode,
        type: "sms",
      });
      if (error) {
        throw error;
      }
      trackEvent("existing_whatsapp_otp_verified", {
        code: getAttributionCode(),
      });
      clearShareLinkEntry({ preserveReferrerName: true });
      onSuccess({ accountId: existingOtpAccountIdRef.current, setupMode: existingOtpSetupModeRef.current });
    } catch (error) {
      setExistingOtpError(error?.message || "That code didn’t work. Request a new one.");
    } finally {
      setIsVerifyingExistingOtp(false);
    }
  };

  const handleBackPress = () => {
    if (isInteractionLocked) {
      return;
    }
    if (
      typeof window !== "undefined"
      && currentLocalHistoryView
      && currentLocalHistoryView !== rootLocalHistoryView
    ) {
      window.history.back();
      return;
    }
    onBack();
  };

  const handleContinueFromLinkWelcome = () => {
    if (isInteractionLocked) {
      return;
    }
    if (typeof window !== "undefined" && window.history.state?.mincoolLinkingView !== LOCAL_HISTORY_VIEWS.welcome) {
      const currentState = window.history.state || {};
      window.history.replaceState(
        { ...currentState, mincoolLinkingView: LOCAL_HISTORY_VIEWS.welcome },
        "",
        window.location.href,
      );
    }
    if (typeof window !== "undefined" && window.history.state?.mincoolLinkingView !== LOCAL_HISTORY_VIEWS.intro) {
      pushLocalHistoryView(LOCAL_HISTORY_VIEWS.intro);
    }
    setShowLinkWelcome(false);
    setShowLinkIntro(true);
    setShowSuggestedAccount(true);
    setShowExistingOtpCard(false);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
  };

  const handleContinueFromLinkIntro = () => {
    if (isInteractionLocked) {
      return;
    }
    if (typeof window !== "undefined" && window.history.state?.mincoolLinkingView !== LOCAL_HISTORY_VIEWS.intro) {
      const currentState = window.history.state || {};
      window.history.replaceState(
        { ...currentState, mincoolLinkingView: LOCAL_HISTORY_VIEWS.intro },
        "",
        window.location.href,
      );
    }
    if (typeof window !== "undefined" && window.history.state?.mincoolLinkingView !== LOCAL_HISTORY_VIEWS.suggested) {
      pushLocalHistoryView(LOCAL_HISTORY_VIEWS.suggested);
    }
    setShowLinkWelcome(false);
    setShowLinkIntro(false);
    setShowSuggestedAccount(true);
    setShowExistingOtpCard(false);
    setShowQrFallback(false);
    setShowNotificationCheckpoint(false);
    setShowWaitingForConfirmation(false);
    setLinkState(STATES.ready);
    setErrorMessage("");
    setExistingOtpError("");
  };

  return (
    <div className="min-h-[100dvh] flex flex-col bg-background">
      <nav className="flex items-center justify-between px-5 py-4">
        <Logo size="sm" />
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBackPress}
          className="text-muted-foreground"
          disabled={isInteractionLocked}
        >
          {tx("Back")}
        </Button>
      </nav>

      <div className="flex-1 flex flex-col items-center justify-center px-5 pb-24">
        <motion.div
          className="w-full max-w-sm text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mb-6 space-y-4">
            <div>
              <h1 className="font-display text-2xl md:text-3xl font-bold">
                {activeCardView === "error"
                  ? tx("Something went wrong")
                  : activeCardView === "expired"
                    ? tx("Link session expired")
                  : activeCardView === "welcome"
                    ? tx(MIN_LINKING_WELCOME_CONTENT.title)
                  : activeCardView === "intro"
                    ? tx("Here’s what happens next")
                  : activeCardView === "suggested"
                  ? suggestedAccountLabel
                    ? tx("Is this your WhatsApp number?")
                    : tx("Is this the WhatsApp number you want to add to Min?")
                  : activeCardView === "otp"
                    ? tx("Check WhatsApp for your code")
                  : activeCardView === "notify"
                    ? tx(MIN_LINKING_TRUST_CHECKPOINT_CONTENT.title)
                  : activeCardView === "waiting"
                    ? tx("Waiting for WhatsApp confirmation")
                  : activeCardView === "qr"
                    ? tx("Link with QR code")
                    : tx("Link with phone number")}
              </h1>
              {(activeCardView === "welcome" || activeCardView === "qr" || activeCardView === "otp" || activeCardView === "waiting" || activeCardView === "intro") && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {activeCardView === "welcome"
                    ? tx(MIN_LINKING_WELCOME_CONTENT.body)
                    : activeCardView === "intro"
                    ? tx("Link WhatsApp first, then finish setup.")
                    : activeCardView === "qr"
                    ? tx("Scan the code in WhatsApp.")
                    : activeCardView === "otp"
                      ? tx("We sent the sign-in code to your own WhatsApp chat.")
                    : activeCardView === "waiting"
                      ? tx("We’ll keep checking in the background.")
                    : tx("Get the code. Paste it into WhatsApp. That's it.")}
                </p>
              )}
            </div>
            {activeCardView === "intro" && suggestedSocialProofLabel ? (
              <div
                className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-left"
                data-testid="linking-social-proof"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                    <Lightbulb className="h-4 w-4" strokeWidth={2.2} />
                  </div>
                  <p className="pt-0.5 text-sm font-medium leading-relaxed text-foreground">
                    {tx(suggestedSocialProofLabel)}
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="relative bg-card border border-border rounded-3xl p-6 mb-6 mx-auto flex min-h-[280px] items-center justify-center">
            <AnimatePresence mode="wait">
              {activeCardView === "welcome" && (
                <motion.div
                  key="welcome"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex w-full flex-col items-center gap-5"
                >
                  <LinkingWelcomePanel
                    disabled={isInteractionLocked}
                    onContinue={handleContinueFromLinkWelcome}
                  />
                </motion.div>
              )}

              {activeCardView === "intro" && (
                <motion.div
                  key="intro"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex w-full flex-col items-center gap-5"
                  data-testid="linking-intro-panel"
                >
                  <LinkingNextSteps />
                  <Button
                    className="h-12 w-full rounded-xl"
                    onClick={handleContinueFromLinkIntro}
                    disabled={isInteractionLocked}
                    data-testid="linking-intro-continue"
                  >
                    {tx("Continue")}
                  </Button>
                </motion.div>
              )}

              {activeCardView === "suggested" && (
                <motion.div key="suggested" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4 w-full">
                  <div className="group relative">
                    <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <Smartphone className="w-7 h-7" />
                    </div>
                    {isBootstrappingSession && (
                      <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 hidden -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-border/70 bg-muted/95 px-3 py-2 text-xs text-muted-foreground shadow-sm group-hover:inline-flex">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {tx("Connecting to WhatsApp")}
                      </div>
                    )}
                  </div>
                  <div className="w-full rounded-3xl border border-border/80 bg-muted/30 px-5 py-5 text-center">
                    {suggestedMaskedPhoneLabel ? (
                      <div className="inline-flex items-center justify-center rounded-full border border-border/80 bg-background/80 px-4 py-2 text-base font-semibold text-foreground">
                        {suggestedMaskedPhoneLabel}
                      </div>
                    ) : null}
                    {suggestedAccountLabel ? (
                      <p className="mt-3 text-xl font-semibold text-foreground">{suggestedAccountLabel}</p>
                    ) : null}
                  </div>
                  <div className="grid w-full grid-cols-2 gap-3">
                    <Button
                      variant="ghost"
                      onClick={handleUseAnotherAccount}
                      className="h-12 w-full rounded-xl"
                      disabled={isInteractionLocked}
                      data-testid="share-link-suggested-no"
                    >
                      {tx("No")}
                    </Button>
                    <Button
                      onClick={handleUseSuggestedAccount}
                      className="h-12 w-full min-w-0 rounded-xl px-3 text-[13px] sm:text-sm"
                      disabled={isInteractionLocked}
                      data-testid="share-link-suggested-yes"
                    >
                      {isBlockedExistingMinAccount && isStartingExistingOtp ? (
                        <span className="inline-flex max-w-full items-center justify-center gap-1.5 overflow-hidden">
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                          <span className="truncate">{tx("Sending code…")}</span>
                        </span>
                      ) : !isBlockedExistingMinAccount && isRequestingCode ? (
                        <span className="inline-flex max-w-full items-center justify-center gap-1.5 overflow-hidden">
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                          <span className="truncate">{tx("Getting code…")}</span>
                        </span>
                      ) : !isBlockedExistingMinAccount && isQueuedCodeRequest ? (
                        <span className="inline-flex max-w-full items-center justify-center gap-1.5 overflow-hidden">
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                          <span className="truncate">{tx("Preparing code…")}</span>
                        </span>
                      ) : (
                        tx("Yes")
                      )}
                    </Button>
                  </div>
                  {existingOtpError ? (
                    <p className="text-xs text-destructive">{existingOtpError}</p>
                  ) : null}
                </motion.div>
              )}

              {activeCardView === "notify" && (
                <motion.div key="notify" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="w-full">
                  <LinkingTrustCheckpoint
                    disabled={isInteractionLocked}
                    onContinue={() => handleContinueToCodeAfterNotification({
                      showFullInstructions: true,
                      showInstructionsToggle: false,
                    })}
                  />
                </motion.div>
              )}

              {activeCardView === "otp" && (
                <motion.div
                  key="otp"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex w-full flex-col items-center gap-4"
                  data-testid="existing-whatsapp-otp-panel"
                >
                  <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <ShieldCheck className="w-7 h-7" />
                  </div>
                  <div className="w-full rounded-2xl border border-border bg-muted/50 px-4 py-3 text-left">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{tx("WhatsApp number")}</p>
                    <p className="mt-2 rounded-full border border-border/80 bg-background/80 px-4 py-2 text-base font-semibold text-foreground">
                      {existingOtpMaskedPhoneNumber || "•••• ••••"}
                    </p>
                  </div>
                  <div className="w-full space-y-2 text-left">
                    <label htmlFor="existing-otp-code" className="text-sm font-medium text-foreground">{tx("6-digit code")}</label>
                    <Input
                      id="existing-otp-code"
                      value={existingOtpCode}
                      onChange={handleExistingOtpCodeChange}
                      disabled={isInteractionLocked}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="123456"
                      className="h-12 rounded-2xl text-center text-lg tracking-[0.24em]"
                    />
                  </div>
                  <Button
                    className="h-12 w-full rounded-xl"
                    onClick={() => void handleVerifyExistingOtp()}
                    disabled={existingOtpCode.length !== 6 || isInteractionLocked}
                  >
                    {isVerifyingExistingOtp ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {tx("Verifying…")}
                      </>
                    ) : (
                      tx("Continue with code")
                    )}
                  </Button>
                  <div className="grid w-full grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      className="h-11 rounded-xl"
                      type="button"
                      onClick={(event) => {
                        if (canUseWhatsappScheme) {
                          handleOpenExistingOtpWhatsApp(event);
                          return;
                        }
                        if (typeof window !== "undefined") {
                          window.open(existingOtpWebLink, "_blank", "noopener,noreferrer");
                        }
                      }}
                      disabled={isInteractionLocked}
                    >
                      {tx("Open WhatsApp")}
                    </Button>
                    <Button
                      variant="ghost"
                      className="h-11 rounded-xl"
                      onClick={handleUseEmailInstead}
                      disabled={isInteractionLocked}
                    >
                      {tx("Use email instead")}
                    </Button>
                  </div>
                  {existingOtpError ? (
                    <p className="text-xs text-destructive">{existingOtpError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">{tx("Enter the code from your personal WhatsApp chat.")}</p>
                  )}
                </motion.div>
              )}

              {activeCardView === "qr" && (
                <motion.div key="qr" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4 w-full">
                  {isMobileQrLayout ? (
                    <>
                      <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <QrCode className="w-7 h-7" />
                      </div>
                      <div className="flex w-full items-center justify-between gap-3">
                        <p className="text-sm text-muted-foreground">{tx("Show this on another device")}</p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-9 rounded-xl px-3 text-sm text-muted-foreground"
                          onClick={() => void handleCopyQrLink()}
                        >
                          {copyLinkState === "copied" ? (
                            <>
                              <Check className="mr-2 h-4 w-4" />
                              {tx("Copied")}
                            </>
                          ) : (
                            <>
                              <Copy className="mr-2 h-4 w-4" />
                              {tx("Copy link")}
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex w-full items-center justify-between gap-4">
                      <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <QrCode className="w-7 h-7" />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 rounded-xl px-3 text-sm text-muted-foreground"
                        onClick={() => void handleCopyQrLink()}
                      >
                        {copyLinkState === "copied" ? (
                          <>
                            <Check className="mr-2 h-4 w-4" />
                            {tx("Copied")}
                          </>
                        ) : (
                          <>
                            <Copy className="mr-2 h-4 w-4" />
                            {tx("Copy link")}
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  <div className="w-full rounded-2xl border border-border bg-muted/50 px-4 py-4">
                    {qrImage ? (
                      <img src={qrImage} alt={tx("WhatsApp QR code")} className="mx-auto rounded-xl bg-white p-3 w-56 h-56 object-contain" />
                    ) : (
                      <div className="w-56 h-56 mx-auto rounded-xl border border-dashed border-border flex flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
                        <span>{tx("Loading QR…")}</span>
                      </div>
                    )}
                  </div>
                  <div className="grid w-full gap-2 text-left text-sm text-muted-foreground">
                    {[
                      { label: <>{tx("Open")} <span className="font-semibold text-foreground">WhatsApp{isMobileQrLayout ? "" : ` ${tx("on your phone")}`}</span></>, href: canUseWhatsappScheme ? "whatsapp://send" : OPEN_WHATSAPP_URL, key: "open-whatsapp" },
                      { label: <>{tx("Tap")} <span className="font-semibold text-foreground">{tx("'Settings'")}</span></>, key: "settings" },
                      { label: <>{tx("Tap")} <span className="font-semibold text-foreground">{tx("'Linked Devices'")}</span></>, key: "linked-devices" },
                      { label: <>{tx("Tap")} <span className="font-semibold text-foreground">{tx("'+ Link a device'")}</span></>, key: "link-device" },
                      { label: <span className="font-semibold text-foreground">{tx("Scan the QR code above")}</span>, key: "scan-code" },
                    ].map((step, index) => {
                      const { label, href = "", key } = step;
                      const stepBody = (
                        <>
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary">
                            {index + 1}
                          </div>
                          <p>{label}</p>
                        </>
                      );

                      if (href) {
                        return (
                          <a
                            key={key}
                            href={href}
                            target={canUseWhatsappScheme ? undefined : "_blank"}
                            rel={canUseWhatsappScheme ? undefined : "noreferrer"}
                            className="flex items-center gap-3 rounded-2xl border border-border/70 bg-muted/40 px-3 py-3 transition-colors hover:border-primary/40 hover:bg-primary/5"
                            onClick={href.includes("whatsapp://") ? handleOpenWhatsApp : undefined}
                          >
                            {stepBody}
                          </a>
                        );
                      }

                      return (
                        <div key={key} className="flex items-center gap-3 rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
                          {stepBody}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {activeCardView === "ready" && (
                <motion.div key="ready" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4 w-full">
                  <div className="group relative">
                    <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                      <Smartphone className="w-7 h-7" />
                    </div>
                    {isBootstrappingSession && (
                      <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 hidden -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-full border border-border/70 bg-muted/95 px-3 py-2 text-xs text-muted-foreground shadow-sm group-hover:inline-flex">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {tx("Connecting to WhatsApp")}
                      </div>
                    )}
                  </div>
                  <>
                    <div className="space-y-2 w-full">
                      <label htmlFor="phone-number" className="text-sm font-medium text-left block">
                        {tx("Your WhatsApp number")}
                      </label>
                      <PhoneNumberField
                        id="phone-number"
                        defaultCountry={defaultCountry}
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        disabled={isInteractionLocked}
                      />
                    </div>
                    <Button
                      onClick={() => void handleRequestCode(phoneNumber, { checkpointAfterRequest: true })}
                      className="h-12 w-full min-w-0 rounded-xl px-3 text-[13px] sm:text-sm"
                      disabled={!canSubmitPhoneNumber || isInteractionLocked}
                    >
                      {isRequestingCode ? (
                        <span className="inline-flex max-w-full items-center justify-center gap-1.5 overflow-hidden">
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                          <span className="truncate">{tx("Getting code…")}</span>
                        </span>
                      ) : isQueuedCodeRequest ? (
                        <span className="inline-flex max-w-full items-center justify-center gap-1.5 overflow-hidden">
                          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                          <span className="truncate">{tx("Preparing code…")}</span>
                        </span>
                      ) : (
                        tx("Get code")
                      )}
                    </Button>
                    {isPendingCodeSubmission && (
                      <div
                        data-testid="request-code-delay-notice"
                        className="w-full rounded-xl border border-amber-200/80 bg-amber-50 px-3 py-2 text-left text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
                      >
                        {tx("This can take up to 60 seconds.")}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">{helperText}</p>
                  </>
                </motion.div>
              )}

              {activeCardView === "code" && (
                <motion.div
                  key="awaiting"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center gap-4 w-full"
                  data-testid="pairing-code-panel"
                >
                  <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <ShieldCheck className="w-7 h-7" />
                  </div>
                  <div className="w-full rounded-2xl border border-border bg-muted/50 px-4 py-3 text-left">
                    <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{tx("Selected number")}</p>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <div className="min-w-0 rounded-full border border-border/80 bg-background/80 px-4 py-2 text-base font-semibold text-foreground">
                        {maskedSelectedInternationalPhone || maskedSelectedCodePhone || "••• ••• ••••"}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 rounded-lg px-2 shrink-0"
                        onClick={handleChangeNumber}
                        disabled={isInteractionLocked}
                      >
                        <PencilLine className="w-4 h-4 mr-1" />
                        {tx("Change")}
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      {pairingCode ? tx("Enter this inside WhatsApp") : tx("Getting your code for this number")}
                    </p>
                    <div className="rounded-2xl border border-border bg-background px-6 py-4 font-mono text-3xl tracking-[0.35em] text-foreground">
                      {pairingCode || "··· ···"}
                    </div>
                  </div>
                  <div className="w-full space-y-3">
                    {showPairingCodeInstructionsToggle || !showPairingCodeInstructions ? (
                      <Button
                        type="button"
                        variant="ghost"
                        className="h-10 rounded-xl px-0 text-sm font-medium text-muted-foreground"
                        onClick={() => setShowPairingCodeInstructions((current) => !current)}
                      >
                        {showPairingCodeInstructions ? tx("Hide full instructions") : tx("Show full instructions")}
                      </Button>
                    ) : null}
                    {showPairingCodeInstructions ? (
                      <div className="grid w-full gap-2 text-left text-sm text-muted-foreground">
                        <a
                          href={canUseWhatsappScheme ? "whatsapp://send" : OPEN_WHATSAPP_URL}
                          target={canUseWhatsappScheme ? undefined : "_blank"}
                          rel={canUseWhatsappScheme ? undefined : "noreferrer"}
                          className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3 transition-colors hover:border-primary/40 hover:bg-primary/5"
                          onClick={canUseWhatsappScheme ? handleOpenWhatsApp : undefined}
                        >
                          {tx("1. Open")} <span className="font-semibold text-foreground">WhatsApp</span>
                        </a>
                        <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">{tx("2. Tap")} <span className="font-semibold text-foreground">{tx("'Linked Devices'")}</span></div>
                        <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">{tx("3. Tap")} <span className="font-medium text-foreground">{tx("'+ Link a device'")}</span></div>
                        <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">{tx("4. Tap")} <span className="font-medium text-foreground">{tx("'Link with phone number instead'")}</span></div>
                        <div className="rounded-2xl border border-border/70 bg-muted/40 px-3 py-3">
                          {tx("5. Enter")} <span className="font-semibold text-foreground">{pairingCode || "··· ···"}</span>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <Button
                    className="w-full rounded-xl h-11"
                    disabled={!pairingCode || isInteractionLocked}
                    onClick={handleContinueToWaitingForConfirmation}
                    data-testid="pairing-code-next"
                  >
                    {tx("Next")}
                  </Button>
                  <p className="text-xs text-muted-foreground">{tx("After you enter the code in WhatsApp, tap Next. Wrong number? Change it and request a new code.")}</p>
                </motion.div>
              )}

              {activeCardView === "waiting" && (
                <motion.div
                  key="waiting"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center gap-4 w-full"
                  data-testid="link-waiting-panel"
                >
                  <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Loader2 className="w-7 h-7 animate-spin" />
                  </div>
                  <div className="w-full rounded-2xl border border-border bg-muted/50 px-4 py-4 text-center">
                    <p className="text-base font-semibold text-foreground">{tx("Waiting for WhatsApp confirmation")}</p>
                    <p className="mt-2 text-sm text-muted-foreground">{tx("We check every two seconds. This can take up to 60 seconds.")}</p>
                  </div>
                  {ONBOARDING_DESIGN_MODE && (
                    <Button
                      variant="outline"
                      className="w-full rounded-xl h-11"
                      disabled={!isDesignLinkReady || isInteractionLocked}
                      onClick={finalizeLinkedSuccess}
                      data-testid="finish-test-link"
                    >
                      {isDesignLinkReady ? tx("Finish test link") : tx("Waiting for test link to be ready…")}
                    </Button>
                  )}
                </motion.div>
              )}

              {activeCardView === "expired" && (
                <motion.div key="expired" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                    <RefreshCw className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">{tx("Link session expired")}</p>
                    <p className="text-sm text-muted-foreground">{tx("Start a fresh session and request a new code.")}</p>
                  </div>
                  <Button onClick={initSession} className="rounded-xl" disabled={isResettingSession}>
                    {isResettingSession ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {tx("Getting new code…")}
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" /> {tx("Get new code")}
                      </>
                    )}
                  </Button>
                </motion.div>
              )}

              {activeCardView === "error" && (
                <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4">
                  <AlertCircle className="w-12 h-12 text-destructive" />
                  <div>
                    <p className="font-semibold mb-1">{tx("Something went wrong")}</p>
                    <p className="text-sm text-muted-foreground">{errorMessage ? tx(errorMessage) : tx("Please try again.")}</p>
                  </div>
                  <Button onClick={initSession} variant="outline" className="rounded-xl" disabled={isResettingSession}>
                    {isResettingSession ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {tx("Trying again…")}
                      </>
                    ) : (
                      tx("Try again")
                    )}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {activeCardView !== "otp" && activeCardView !== "intro" && activeCardView !== "welcome" ? (
            <div className="mb-4">
              <Button
                variant="ghost"
                className="text-sm text-muted-foreground gap-2"
                onClick={shouldShowQrMode ? handleShowCodeEntry : handleShowQr}
                disabled={isInteractionLocked}
              >
                {shouldShowQrMode ? <Smartphone className="w-4 h-4" /> : <QrCode className="w-4 h-4" />}
                {shouldShowQrMode ? tx("Use code instead") : tx("Use QR instead")}
              </Button>
            </div>
          ) : null}

          <div className="mt-6">
            <Button
              variant="ghost"
              className="mx-auto text-sm text-muted-foreground"
              onClick={onSignIn}
              disabled={isInteractionLocked}
            >
              {tx("Already have an account? Sign in")}
            </Button>
          </div>
        </motion.div>
      </div>

    </div>
  );
}
